import firebase from 'firebase/app';
import 'firebase/storage';

/**
 * Gets all the documents in animatedIconNameComponentCategories collection
 *
 * @export
 * @return {Promise} - Promise which succeeds once the request is successful
 */
export async function getAnimatedIconNameComponentCategories() {
  var db = firebase.firestore();
  return await db.collection('animatedIconNameComponentCategories').get();
}

/**
 * Gets the details of the animatedIcon document with uid as animatedIconUid
 *
 * @export
 * @param {String} animatedIconUid - UID of the animatedIcon document
 * @return {Promise} - Promise which succeeds once the request is successful. The promise contains the document data object.
 */
export async function getAnimatedIconDetails(animatedIconUid) {
  if (animatedIconUid) {
    var db = firebase.firestore();
    var doc = await db.collection('animated_icons').doc(animatedIconUid).get();
    if (doc.exists) {
      return {
        ...doc.data(),
        id: doc.id,
      };
    } else {
      throw new Error('AnimatedIcon with this ID doesnt exist');
    }
  } else {
    throw new Error('Invalid AnimatedIcon ID');
  }
}

/**
 * Creates a new AnimatedIcon
 * @export
 * @param {Object} name - Name object of the AnimatedIcon
 * @param {File} animatedIconFile - Svg file of the AnimatedIcon
 * @param {String} userId - Id of the user obtained from the user object
 * @return {Promise} - Promise which succeeds once the request is successful. The promise contains the newly created animatedIcon's uid.
 */
export async function createAnimatedIcon(name, animatedIconFile, userId) {
  var combinedName = getCombinedName(name);
  console.log(combinedName);
  // Check if AnimatedIcon name is unique
  await isAnimatedIconNameUnique(combinedName);
  console.log('isAnimatedIconNameUnique success');
  // Create AnimatedIcon record on firebase
  var recordId = await createAnimatedIconRecordOnDb(name, combinedName, userId);
  console.log('createAnimatedIconRecordOnDb success');
  // upload image to firebase storage
  var fileUrl = await uploadAnimatedIconFile(combinedName, animatedIconFile);
  // Add url to uploaded image to created record
  await addFileUrlForAnimatedIcon(recordId, userId, fileUrl);
  return recordId;
}

/**
 * Updates an already created animatedIcon
 *
 * @export
 * @param {String} userId - Id of the user obtained from the user object
 * @param {String} animatedIconId - UID of the animatedIcon document
 * @param {File} animatedIconFile - Svg file of the animatedIcon
 * @param {String} combinedName - Combined name of the animatedIcon. getCombinedName() method can be used to obtain the combined name from the name object.
 * @return {Promise} Promise which succeeds once the request is successful.
 */
export async function updateAnimatedIcon(
  userId,
  animatedIconId,
  animatedIconFile,
  combinedName
) {
  // upload image to firebase storage
  var fileUrl = await uploadAnimatedIconFile(combinedName, animatedIconFile);
  // Update url of uploaded image to created record
  return await addFileUrlForAnimatedIcon(animatedIconId, userId, fileUrl);
}

/**
 * Returns the combined animatedIcon name given the animatedIcon Name object
 *
 * @export
 * @param {Object} name - Name object of the animatedIcon. Should contain 3 keys : product_category, animated_illustration_category, animated_illustration_name
 * @return {String} - The combined name string of the AnimatedIcon
 */
export function getCombinedName(name) {
  return `${name.animated_icon_category}_${name.animated_icon_size}_${name.animated_icon_name}`;
}

/**
 * Checks if the AnimatedIcon with the given name already exists or not
 *
 * @param {String} combinedName - Combined name of the AnimatedIcon. getCombinedName() method can be used to obtain the combined name from the name object.
 * @return {Promise} - Promise which succeeds if the AnimatedIcon name is unique else throws an error
 */
async function isAnimatedIconNameUnique(combinedName) {
  // check on firebase if animatedIcon with same name already exists
  var db = firebase.firestore();
  var querySnapshot = await db
    .collection('animated_icons')
    .where('combined_name', '==', combinedName)
    .limit(1)
    .get();
  if (querySnapshot.size > 0) {
    throw new Error('AnimatedIcon with the same name already exists');
  } else {
    return;
  }
}

/**
 * Created document for an animatedIcon on the database
 *
 * @param {Object} name - Name object of the animatedIcon
 * @param {String} combinedName - Combined name of the animatedIcon. getCombinedName() method can be used to obtain the combined name from the name object.
 * @param {String} userId - Id of the user obtained from the user object
 * @return {Promise} - Promise which succeeds when the animatedIcon is successfully created, else throws an error.
 */
async function createAnimatedIconRecordOnDb(name, combinedName, userId) {
  var db = firebase.firestore();
  var animatedIconsCollectionRef = db.collection('animated_icons');

  var animatedIconObj = {
    name: name,
    combined_name: combinedName,
    created_at: firebase.firestore.FieldValue.serverTimestamp(),
    created_by: userId,
    last_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
    last_updated_by: userId,
  };

  const docRef = await animatedIconsCollectionRef.add(animatedIconObj);
  console.log('Animated Illustrtion record creation success' + docRef.id);
  return docRef.id;
}

/**
 * Updates animatedIcon document's image_url field
 *
 * @param {String} recordId - UID of the animatedIcon document
 * @param {String} userId - Id of the user obtained from the user object
 * @param {String} fileUrl - Url of the uploaded file
 * @return {Promise} - Promise that succeeds when the updation of the field is successful.
 */
async function addFileUrlForAnimatedIcon(recordId, userId, fileUrl) {
  var db = firebase.firestore();
  var animatedIconsCollectionRef = db.collection('animated_icons');

  return await animatedIconsCollectionRef.doc(recordId).update({
    img_url: fileUrl,
    last_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
    last_updated_by: userId,
  });
}

/**
 * Uploads an animatedIcon file to storage and returns url of the uploaded file
 *
 * @param {String} combinedAnimatedIconName - Combined name of the animatedIcon. getCombinedName() method can be used to obtain the combined name from the name object.
 * @param {File} file - Svg file of the animatedIcon
 * @return {Promise} - Promise that succeeds once the uploading of the animatedIcon file is successful. The promise contains the download url of the file.
 */
async function uploadAnimatedIconFile(combinedAnimatedIconName, file) {
  // Identifier could be default or region identifier or event identifier
  const storage = firebase.storage();
  var animatedIconsRootFolderRef = storage.ref('coffer/animated_icons');
  var animatedIconImageRef = animatedIconsRootFolderRef.child(
    `${combinedAnimatedIconName}.json`
  );
  const maxAge = 90 * 24 * 60 * 60; // days * hours * minutes * seconds
  var metadata = {
    cacheControl: `max-age=${maxAge}`,
  };
  await animatedIconImageRef.put(file, metadata);
  const url = await animatedIconImageRef.getDownloadURL();
  return url;
}
